import { useQuery } from "@tanstack/react-query";
import { useIndexedDBStore } from "../libs/use-indexeddb/";
import moment from "moment";
import { dataset as f10Data } from "../data/f10";
import { dataset as m11Data } from "../data/m11";
import { useAppStore } from "../store/appStore";
import { pad } from "../fn/pad";

export const roundTimeRange = {
	start: 1661054400,
	trendsStart: 1661068800,
	end: 1661612460,
};

const sortVotes = (left, right) => {
	if (left.rank < right.rank) {
		return -1;
	} else if (left.rank > right.rank) {
		return 1;
	} else {
		return left.id < right.id ? -1 : 1;
	}
};

export const useQueryVoteResult = ({ listOption, sortEnabled }) => {
	const queryFn = async () => {
		const result =
			listOption === "f"
				? [
						{ id: 2, votes: 0.091, totalVotes: 4536, rank: 6 },
						{ id: 3, votes: 0.0961, totalVotes: 4788, rank: 2 },
						{ id: 4, votes: 0.1281, totalVotes: 6383, rank: 1 },
						{ id: 6, votes: 0.091, totalVotes: 4534, rank: 7 },
						{ id: 7, votes: 0.0911, totalVotes: 4540, rank: 5 },
						{ id: 12, votes: 0.1346, totalVotes: 6711, rank: 0 },
						{ id: 14, votes: 0.0909, totalVotes: 4531, rank: 8 },
						{ id: 15, votes: 0.0909, totalVotes: 4530, rank: 9 },
						{ id: 17, votes: 0.0952, totalVotes: 4746, rank: 3 },
						{ id: 26, votes: 0.0911, totalVotes: 4543, rank: 4 },
				  ]
				: [
						{ id: 27, votes: 0.0893, totalVotes: 4535, rank: 4 },
						{ id: 28, votes: 0.0963, totalVotes: 4895, rank: 1 },
						{ id: 32, votes: 0.0916, totalVotes: 4653, rank: 2 },
						{ id: 33, votes: 0.0889, totalVotes: 4517, rank: 9 },
						{ id: 34, votes: 0.0894, totalVotes: 4540, rank: 3 },
						{ id: 37, votes: 0.0891, totalVotes: 4526, rank: 7 },
						{ id: 43, votes: 0.0999, totalVotes: 5075, rank: 0 },
						{ id: 45, votes: 0.0891, totalVotes: 4525, rank: 8 },
						{ id: 46, votes: 0.0892, totalVotes: 4534, rank: 5 },
						{ id: 49, votes: 0.0892, totalVotes: 4531, rank: 6 },
						{ id: 51, votes: 0.0881, totalVotes: 4474, rank: 10 },
				  ];

		result.forEach((choice, index) => {
			result[index].votes = choice.votes * 100.0;
			result[index].rank = choice.rank + 1;
		});

		return {
			records: sortEnabled
				? [
						...result.sort((left, right) => {
							return left.rank < right.rank ? -1 : 1;
						}),
				  ]
				: result,
			lastRefreshTime: moment("2022-08-28 23:00:00"),
		};
	};

	return useQuery(["top21VoteResult", { listOption, sortEnabled }], queryFn, {
		retry: false,
	});
};

export const useQueryTrend = ({
	listOption,
	timeRangeMin,
	timeRangeMax,
	timeInterval,
	timeFilter,
}) => {
	const setQueryTop21TrendsProgress = useAppStore(
		(state) => state.setQueryTop21TrendsProgress
	);

	const dataFiles = [
		"1661054400.json",
		"1661140800.json",
		"1661227200.json",
		"1661313600.json",
		"1661400000.json",
		"1661486400.json",
		"1661572800.json",
	];

	const { getByID, add } = useIndexedDBStore("top21-grouped");

	const queryFn = async () => {
		setQueryTop21TrendsProgress(listOption, 0);

		// ---- rank trends
		const rankTrendsTimeGroups = [];

		const now = moment();
		const initial = moment.unix(roundTimeRange.start - 1);
		const max = moment.unix(roundTimeRange.end);
		const estFinal = moment
			.unix(
				timeFilter !== "custom" || timeRangeMax === "-"
					? now.unix()
					: timeRangeMax
			)
			.isAfter(max)
			? max
			: moment.unix(
					timeFilter !== "custom" || timeRangeMax === "-"
						? now.unix()
						: timeRangeMax
			  );
		const final = now.isBefore(estFinal) ? now : estFinal;

		const min =
			timeFilter === "last-24-hours"
				? moment(now.isSameOrBefore(final) ? now : final).subtract(1, "days")
				: timeFilter === "last-12-hours"
				? moment(now.isSameOrBefore(final) ? now : final).subtract(12, "hours")
				: timeFilter === "last-6-hours"
				? moment(now.isSameOrBefore(final) ? now : final).subtract(6, "hours")
				: timeFilter === "last-1-hours"
				? moment(now.isSameOrBefore(final) ? now : final).subtract(1, "hours")
				: timeFilter === "last-30-minutes"
				? moment(now.isSameOrBefore(final) ? now : final).subtract(
						30,
						"minutes"
				  )
				: moment.unix(roundTimeRange.trendsStart);

		const first = moment.unix(timeRangeMin).isBefore(min)
			? min
			: moment.unix(timeRangeMin);

		let rankTrendsGroupTime = moment(first);
		while (rankTrendsGroupTime.isBefore(final)) {
			rankTrendsTimeGroups.push({
				time: moment(rankTrendsGroupTime),
				unixTime: moment(rankTrendsGroupTime).unix(),
			});
			rankTrendsGroupTime = moment(rankTrendsGroupTime).add(
				timeInterval.includes("01-")
					? 1
					: timeInterval.includes("02-")
					? 2
					: timeInterval.includes("03-")
					? 3
					: timeInterval.includes("04-")
					? 4
					: timeInterval.includes("05-")
					? 5
					: timeInterval.includes("06-")
					? 6
					: timeInterval.includes("10-")
					? 10
					: timeInterval.includes("12-")
					? 12
					: timeInterval.includes("15-")
					? 15
					: timeInterval.includes("20-")
					? 20
					: timeInterval.includes("30-")
					? 30
					: 1,
				timeInterval.includes("day")
					? "day"
					: timeInterval.includes("minute")
					? "minutes"
					: timeInterval.includes("hour")
					? "hours"
					: "hours"
			);
		}
		rankTrendsTimeGroups.push({
			time: final,
			unixTime: final.unix(),
			data: [],
		});

		const rankTrendsTimeGroupsOriginal = rankTrendsTimeGroups.map((item) => ({
			...item,
		}));
		// -----

		const result = (listOption === "f" ? [...f10Data] : [...m11Data]).map(
			(item, index) => {
				return {
					...item,
					votes: [],
					ranks: [],
					rankTrends: [
						{
							x: "",
							unixTime: initial.unix(),
							y: index + 1,
						},
						...rankTrendsTimeGroups.map((group) => ({
							x: group.time.isSame(final)
								? group.time.isSame(estFinal)
									? max.isSame(estFinal)
										? "FINAL"
										: moment.unix(group.unixTime).format("DD MMM HH:mm")
									: max.isSame(estFinal)
									? "LIVE"
									: moment.unix(group.unixTime).format("DD MMM HH:mm")
								: moment.unix(group.unixTime).format("DD MMM HH:mm"),
							unixTime: group.unixTime,
						})),
					],
				};
			}
		);

		let lastRefreshTime = null;

		const allData = {};

		for (var filename of dataFiles) {
			const cahceKey = `top21-grouped/${filename}`;

			let data = null;

			try {
				const cachedDataRaw = await getByID(cahceKey);
				const cachedData = JSON.parse(cachedDataRaw.content);
				data = cachedData;
			} catch (e) {
				console.log(`fail to get ${cahceKey} from IndexedDB`, e.message);
			}

			if (!data) {
				const dlRaw = await fetch(
					`https://raw.githubusercontent.com/sonoy/tne-the-next-era-2022-stats-dataset/main/top21-grouped/${filename}`
				);
				const dlData = await dlRaw.json();
				try {
					await add({ id: cahceKey, content: JSON.stringify(dlData) });
				} catch (e) {
					console.log(`fail to save ${cahceKey} into IndexedDB`, e.message);
				}

				data = dlData;
			}

			data.forEach((timeData) => {
				allData[timeData.unixTime] = timeData;
			});

			setQueryTop21TrendsProgress(
				listOption,
				((dataFiles.indexOf(filename) + 1) / dataFiles.length) * 50
			);
		}

		const allDataKeys = Object.keys(allData);

		for (var fileIdx of allDataKeys) {
			let data = allData[fileIdx];

			const fileTime = moment.unix(fileIdx);

			if (
				moment(fileTime).add(5, "minutes").isBefore(first) ||
				fileTime.isAfter(final)
			) {
				continue;
			}

			const voteResult = listOption === "f" ? data.fResult : data.mResult;

			const sortedVoteResult = [...voteResult].sort(sortVotes);

			result.forEach((choice, i) => {
				const filteredVoteResult = voteResult.filter(
					({ id }) => choice.id === id
				);
				if (filteredVoteResult.length > 0) {
					result[i].votes.push({
						x: moment.unix(data.unixTime).format("YYYY-MM-DD HH:mm:00"),
						y: filteredVoteResult[0].votes,
						rank: filteredVoteResult[0].rank,
					});

					result[i].ranks.push({
						x: moment.unix(data.unixTime).format("YYYY-MM-DD HH:mm:00"),
						y: filteredVoteResult[0].rank,
					});

					result[i].rankTrends.forEach((trendGroup, groupIdx) => {
						if (
							moment
								.unix(data.unixTime)
								.isSameOrBefore(moment.unix(trendGroup.unixTime))
						) {
							result[i].rankTrends[groupIdx].y =
								sortedVoteResult.findIndex(({ id }) => choice.id === id) + 1;
						}
					});
				}
			});

			[...rankTrendsTimeGroupsOriginal].forEach((rttGrpOri, rttGrpOriIdx) => {
				if (
					moment
						.unix(data.unixTime)
						.isSameOrBefore(moment.unix(rttGrpOri.unixTime))
				) {
					const voteTrendsGroupOriginal = {
						time: rttGrpOri.time.isSame(final)
							? rttGrpOri.time.isSame(estFinal)
								? "FINAL"
								: "LIVE"
							: moment.unix(rttGrpOri.unixTime).format("DD MMM HH:mm"),
					};

					voteResult.forEach((groupOri) => {
						voteTrendsGroupOriginal[`#${pad(groupOri.id, 2)}`] = (
							groupOri.votes * 100.0
						).toFixed(2);
					});

					rankTrendsTimeGroupsOriginal[rttGrpOriIdx].data =
						voteTrendsGroupOriginal;
				}
			});

			[...rankTrendsTimeGroups].forEach((rttGrp, rttGrpIdx) => {
				if (
					moment
						.unix(data.unixTime)
						.isSameOrBefore(moment.unix(rttGrp.unixTime))
				) {
					const voteTrendsGroup = {
						time: rttGrp.time.isSame(final)
							? rttGrp.time.isSame(estFinal)
								? "FINAL"
								: "LIVE"
							: moment.unix(rttGrp.unixTime).format("DD MMM HH:mm"),
					};

					let total = 0;
					voteResult.forEach((group) => {
						total += group.votes;
					});

					voteResult.forEach((group) => {
						voteTrendsGroup[`#${pad(group.id, 2)}`] = parseFloat(
							((group.votes / total) * 100.0).toFixed(2)
						);
					});

					rankTrendsTimeGroups[rttGrpIdx].data = voteTrendsGroup;
				}
			});

			lastRefreshTime = moment(data.unixTime);
			setQueryTop21TrendsProgress(
				listOption,
				50 + ((allDataKeys.indexOf(fileIdx) + 1) / allDataKeys.length) * 50
			);
		}

		return {
			records: [
				...result.map((choice) => {
					return {
						...choice,
						votes: [...choice.votes],
						ranks: [...choice.ranks],
						rankTrends: [...choice.rankTrends],
					};
				}),
			],
			voteTrends: rankTrendsTimeGroups.map(({ data }) => data),
			voteTrendsOriginal: rankTrendsTimeGroupsOriginal.map(({ data }) => data),
			lastRefreshTime,
		};
	};

	return useQuery(
		[
			"top21VoteTrends",
			{ listOption, timeRangeMin, timeRangeMax, timeInterval, timeFilter },
		],
		queryFn,
		{
			retry: false,
		}
	);
};

export const useQueryComparison = ({ listOption }) => {
	const queryFn = async () => {
		const result =
			listOption === "f"
				? [
						{ id: 2, votes: 0.091, totalVote: 4536, rank: 6 },
						{ id: 3, votes: 0.0961, totalVote: 4788, rank: 2 },
						{ id: 4, votes: 0.1281, totalVote: 6383, rank: 1 },
						{ id: 6, votes: 0.091, totalVote: 4534, rank: 7 },
						{ id: 7, votes: 0.0911, totalVote: 4540, rank: 5 },
						{ id: 12, votes: 0.1346, totalVote: 6711, rank: 0 },
						{ id: 14, votes: 0.0909, totalVote: 4531, rank: 8 },
						{ id: 15, votes: 0.0909, totalVote: 4530, rank: 9 },
						{ id: 17, votes: 0.0952, totalVote: 4746, rank: 3 },
						{ id: 26, votes: 0.0911, totalVote: 4543, rank: 4 },
				  ]
				: [
						{ id: 27, votes: 0.0893, totalVote: 4535, rank: 4 },
						{ id: 28, votes: 0.0963, totalVote: 4895, rank: 1 },
						{ id: 32, votes: 0.0916, totalVote: 4653, rank: 2 },
						{ id: 33, votes: 0.0889, totalVote: 4517, rank: 9 },
						{ id: 34, votes: 0.0894, totalVote: 4540, rank: 3 },
						{ id: 37, votes: 0.0891, totalVote: 4526, rank: 7 },
						{ id: 43, votes: 0.0999, totalVote: 5075, rank: 0 },
						{ id: 45, votes: 0.0891, totalVote: 4525, rank: 8 },
						{ id: 46, votes: 0.0892, totalVote: 4534, rank: 5 },
						{ id: 49, votes: 0.0892, totalVote: 4531, rank: 6 },
						{ id: 51, votes: 0.0881, totalVote: 4474, rank: 10 },
				  ];

		result.forEach((choice, index) => {
			result[index].votes = choice.votes * 100.0;
		});

		result.forEach((choice, index) => {
			result[index].compare = result.map((vsChoice) => {
				return {
					x: vsChoice.id,
					y: choice.id === vsChoice.id ? null : choice.votes - vsChoice.votes,
				};
			});
		});

		return {
			records: result,
			lastRefreshTime: moment("2022-08-28T23:00:00"),
		};
	};

	return useQuery(["top21VoteResult", { listOption }], queryFn, {
		retry: false,
	});
};
